import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { statsApi, minoriApi, entriesApi, pepApi } from '../services/api'
import OnboardingTour, { useOnboardingTour } from '../components/ui/OnboardingTour'
import ProssimeChiusure from '../components/ui/ProssimeChiusure'
import QuickCheckinWidget from '../components/widgets/QuickCheckinWidget'
import SmartAlertsWidget from '../components/widgets/SmartAlertsWidget'
import PepImportRapido from './minori/PepImportRapido'
import ExperimentalWrapper from '../components/pep/ExperimentalWrapper'
import {
  Baby,
  FileText,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  ArrowRight,
  LayoutDashboard,
  BarChart3,
  Loader2,
  HelpCircle,
  Target,
  AlertTriangle
} from 'lucide-react'

// Helper per estrarre array minori dalla risposta API
const extractMinori = (response) => {
  const data = response?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.minori)) return data.minori
  if (Array.isArray(data?.data?.minori)) return data.data.minori
  return []
}

export default function DashboardPage() {
  const { user, tenant, isCoordinatore } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [mieiMinori, setMieiMinori] = useState([])
  const [tuttiMinori, setTuttiMinori] = useState([])
  const [viewMode, setViewMode] = useState(() => isCoordinatore() ? 'gestionale' : 'educatore') // 'educatore' | 'gestionale'
  const [pepDashboard, setPepDashboard] = useState(null)
  const [showImportRapido, setShowImportRapido] = useState(false)

  // Onboarding Tour
  const { showTour, completeTour, resetTour, startTour } = useOnboardingTour('onboarding_completed')
  
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Carica statistiche (potrebbe fallire se route non esiste)
      let statsData = null
      try {
        const statsRes = await statsApi.dashboard()
        statsData = statsRes.data?.data || statsRes.data
        setStats(statsData)
      } catch (statsError) {
        console.log('Stats API non disponibile:', statsError.message)
        setStats(null)
      }
      
      // Carica dashboard PEP (solo coordinatore, silenzioso se 403)
      if (isCoordinatore()) {
        try {
          const pepRes = await pepApi.getDashboard()
          setPepDashboard(pepRes.data?.data || null)
        } catch {
          // 403 = feature non abilitata, non mostrare widget
          setPepDashboard(null)
        }
      }

      // Carica minori
      const minoriRes = await minoriApi.list({ attivo: true })
      // FIX: Usa helper per estrarre array correttamente
      const allMinori = extractMinori(minoriRes)
      
      // IDs minori NON compilati oggi (forniti dal backend in mieiMinoriNonCompilati)
      const nonCompilatiIds = new Set(
        (statsData?.mieiMinoriNonCompilati || []).map(m => m._id)
      )

      // Filtra i miei minori (assegnati a me) e popola hasEntryToday
      const myMinori = allMinori
        .filter(m =>
          m.educatoreReferente?._id === user?.id ||
          m.educatoreReferente === user?.id
        )
        .map(m => ({
          ...m,
          hasEntryToday: !nonCompilatiIds.has(m._id)
        }))
      
      setMieiMinori(myMinori)
      setTuttiMinori(allMinori)
      
    } catch (error) {
      console.error('Error loading dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Onboarding Tour */}
      <OnboardingTour 
        isOpen={showTour} 
        onComplete={completeTour}
        isCoordinatore={isCoordinatore()}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-tour="welcome">
            Ciao, {user?.nome}! 👋
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {new Date().toLocaleDateString('it-IT', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Bottone per rifare il tour */}
          <button
            onClick={startTour}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Rifai il tour guidato"
          >
            <HelpCircle className="h-5 w-5" />
          </button>

          {/* Toggle vista (solo coordinatore) */}
          {isCoordinatore() && (
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('educatore')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'educatore'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <Baby className="h-4 w-4 inline mr-1" />
                Educatore
              </button>
              <button
                onClick={() => setViewMode('gestionale')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'gestionale'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <BarChart3 className="h-4 w-4 inline mr-1" />
                Gestione
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Import Rapido PEP Modal */}
      {showImportRapido && (
        <PepImportRapido
          onClose={() => setShowImportRapido(false)}
          onDone={loadData}
        />
      )}

      {/* Vista Gestionale (solo coordinatore) */}
      {isCoordinatore() && viewMode === 'gestionale' ? (
        <GestionaleView stats={stats} pepDashboard={pepDashboard} onImportRapido={() => setShowImportRapido(true)} />
      ) : (
        <EducatoreView 
          mieiMinori={mieiMinori} 
          tuttiMinori={tuttiMinori}
          stats={stats}
        />
      )}
    </div>
  )
}

// Vista Educatore
function EducatoreView({ mieiMinori, tuttiMinori, stats }) {
  return (
    <>
      {/* Quick Check-in Widget */}
      <QuickCheckinWidget />

      {/* Prossime Chiusure */}
      <ProssimeChiusure />

      {/* I miei minori assegnati */}
      {mieiMinori.length > 0 && (
        <div className="card p-4" data-tour="minori">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              👥 I Miei Minori Assegnati
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({mieiMinori.length})
              </span>
            </h2>
            {stats?.minoriDaCompilare > 0 && (
              <span className="badge badge-warning">
                {stats.minoriDaCompilare} da compilare
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {mieiMinori.map((minore) => (
              <MinoreCard key={minore._id} minore={minore} />
            ))}
          </div>
        </div>
      )}

      {/* Tutti i minori del centro */}
      <div className="card p-4" data-tour="entries">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            📋 Tutti i Minori del Centro
          </h2>
          <Link 
            to="/minori" 
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            Vedi tutti
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {tuttiMinori.slice(0, 10).map((minore) => (
            <MinoreListItem key={minore._id} minore={minore} />
          ))}
          {tuttiMinori.length === 0 && (
            <p className="py-8 text-center text-gray-500">
              Nessun minore registrato
            </p>
          )}
        </div>
      </div>
    </>
  )
}

// Helper: formatta ora attività con fuso Europe/Rome e data tipo "17/02 20:21"
function formatAttivitaOra(ora) {
  if (!ora) return ''
  try {
    const d = new Date(ora)
    if (isNaN(d.getTime())) return ora // fallback: valore originale
    const formatted = d.toLocaleString('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
    // toLocaleString restituisce "17/02/2025, 20:21" → vogliamo "17/02 20:21"
    return formatted.replace(/\/\d{4},?\s*/, ' ').trim()
  } catch {
    return ora
  }
}

// Vista Gestionale (Coordinatore)
function GestionaleView({ stats, pepDashboard, onImportRapido }) {
  return (
    <>
      {/* Smart Alerts Widget (coordinatori) */}
      <SmartAlertsWidget />

      {/* Prossime Chiusure */}
      <ProssimeChiusure />

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-tour="reports">
        <StatCard
          title="Minori"
          value={stats?.totaleMinori || 0}
          icon={Baby}
          color="bg-blue-500"
        />
        <StatCard
          title="Educatori"
          value={stats?.totaleEducatori || 0}
          icon={Users}
          color="bg-green-500"
        />
        <StatCard
          title="Registrazioni Oggi"
          value={stats?.registrazioniOggi || 0}
          icon={FileText}
          color="bg-purple-500"
        />
        <StatCard
          title="Da Compilare"
          value={stats?.minoriDaCompilare || 0}
          icon={AlertCircle}
          color="bg-orange-500"
        />
      </div>

      {/* PEP KPI + Verifiche in scadenza */}
      {pepDashboard && (
        <ExperimentalWrapper isExperimental={true}>
          <div className="p-4 space-y-4">
            <PepDashboardWidgets pepDashboard={pepDashboard} onImportRapido={onImportRapido} />
          </div>
        </ExperimentalWrapper>
      )}

      {/* Registrazioni mancanti */}
      {stats?.minoriNonCompilati?.length > 0 && (
        <div className="card p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 text-orange-500" />
            Registrazioni Mancanti Oggi
          </h2>
          <div className="space-y-2">
            {stats.minoriNonCompilati.map((minore) => (
              <div 
                key={minore._id}
                className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {minore.nomeCompleto}
                  </p>
                  <p className="text-sm text-gray-500">
                    Ref: {minore.educatoreReferente?.nome || 'Non assegnato'}
                  </p>
                </div>
                <Link
                  to={`/entries/new/${minore._id}`}
                  className="btn-primary text-sm"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Compila
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accessi rapidi - solo mobile */}
      <div className="grid grid-cols-2 gap-4 lg:hidden" data-tour="users">
        <Link to="/minori" className="card p-4 hover:shadow-md transition-shadow text-center">
          <Baby className="h-8 w-8 mx-auto text-primary-600" />
          <p className="mt-2 font-medium text-gray-900 dark:text-white">Gestione Minori</p>
        </Link>
        <Link to="/users" className="card p-4 hover:shadow-md transition-shadow text-center">
          <Users className="h-8 w-8 mx-auto text-green-600" />
          <p className="mt-2 font-medium text-gray-900 dark:text-white">Gestione Utenti</p>
        </Link>
        <Link to="/reports" className="card p-4 hover:shadow-md transition-shadow text-center">
          <BarChart3 className="h-8 w-8 mx-auto text-purple-600" />
          <p className="mt-2 font-medium text-gray-900 dark:text-white">Report</p>
        </Link>
        <Link to="/settings" className="card p-4 hover:shadow-md transition-shadow text-center" data-tour="settings">
          <LayoutDashboard className="h-8 w-8 mx-auto text-orange-600" />
          <p className="mt-2 font-medium text-gray-900 dark:text-white">Impostazioni</p>
        </Link>
      </div>

      {/* Attività recenti */}
      <div className="card p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          📈 Attività Recenti
        </h2>
        {stats?.attivitaRecenti?.length > 0 ? (
          <div className="space-y-3">
            {stats.attivitaRecenti.map((attivita, idx) => (
              <div key={idx} className="flex items-center text-sm">
                <div className="w-2 h-2 rounded-full bg-primary-500 mr-3" />
                <span className="text-gray-600 dark:text-gray-400">
                  <strong>{attivita.educatore}</strong> ha compilato{' '}
                  <strong>{attivita.minore}</strong>
                </span>
                <span className="ml-auto text-gray-400 whitespace-nowrap">
                  {formatAttivitaOra(attivita.ora)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            Nessuna attività recente
          </p>
        )}
      </div>
    </>
  )
}

// ── PEP Dashboard Widgets ────────────────────────────────────
function PepDashboardWidgets({ pepDashboard, onImportRapido }) {
  const navigate = useNavigate()
  const { kpi, verificheScadenza, pepAttivi } = pepDashboard

  // Raccogli verifiche scadute dagli obiettivi attivi
  const oggi = new Date()
  const verificheScadute = []
  for (const pep of (pepAttivi || [])) {
    const minoreNome = pep.minoreId?.nomeCompleto || 'Minore'
    const minoreId = pep.minoreId?._id || pep.minoreId
    for (const ob of (pep.obiettivi || [])) {
      if (ob.stato === 'attivo' && ob.dataScadenzaVerifica && new Date(ob.dataScadenzaVerifica) < oggi) {
        verificheScadute.push({
          minoreId,
          minoreNome,
          obiettivoTitolo: ob.titolo,
          scadenza: ob.dataScadenzaVerifica,
          priorita: ob.priorita
        })
      }
    }
  }

  // Unisci scadute + in scadenza, ordina per data
  const allVerifiche = [
    ...verificheScadute.map(v => ({ ...v, tipo: 'scaduta' })),
    ...(verificheScadenza || []).map(v => {
      const diff = Math.ceil((new Date(v.scadenza) - oggi) / (1000 * 60 * 60 * 24))
      return { ...v, tipo: diff <= 7 ? 'urgente' : 'prossima', giorniRimanenti: diff }
    })
  ].sort((a, b) => new Date(a.scadenza) - new Date(b.scadenza))

  // Calcola giorni rimanenti per le scadute
  for (const v of allVerifiche) {
    if (v.tipo === 'scaduta') {
      v.giorniRimanenti = Math.ceil((new Date(v.scadenza) - oggi) / (1000 * 60 * 60 * 24))
    }
  }

  return (
    <>
      {/* KPI compatte PEP */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Target className="h-4 w-4 text-primary-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">PEP attivi</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpi?.pepAttiviCount || 0}</p>
        </div>
        <div className="card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Obiettivi raggiunti</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpi?.obiettiviRaggiunti || 0}</p>
        </div>
        <div className="card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Verifiche scadute</span>
          </div>
          <p className={`text-2xl font-bold ${(kpi?.verificheScadute || 0) > 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
            {kpi?.verificheScadute || 0}
          </p>
        </div>
      </div>

      {/* Lista verifiche in scadenza */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Target className="h-5 w-5 mr-2 text-primary-500" />
            Verifiche PEP in scadenza
          </h2>
          {onImportRapido && (
            <button
              onClick={onImportRapido}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Import rapido PEP
            </button>
          )}
        </div>

        {allVerifiche.length > 0 ? (
          <div className="space-y-2">
            {allVerifiche.map((v, idx) => {
              const barColor = v.tipo === 'scaduta'
                ? 'bg-red-500'
                : v.tipo === 'urgente'
                  ? 'bg-orange-500'
                  : 'bg-green-500'

              const labelColor = v.tipo === 'scaduta'
                ? 'text-red-600 dark:text-red-400'
                : v.tipo === 'urgente'
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-green-600 dark:text-green-400'

              const giorniLabel = v.giorniRimanenti < 0
                ? `${Math.abs(v.giorniRimanenti)}g scaduta`
                : v.giorniRimanenti === 0
                  ? 'Oggi'
                  : `${v.giorniRimanenti}g`

              return (
                <button
                  key={`${v.minoreId}-${v.obiettivoId || v.obiettivoTitolo}-${idx}`}
                  onClick={() => navigate(`/minori/${v.minoreId}?tab=progetto`)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div className={`w-1 self-stretch rounded-full ${barColor}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {v.minoreNome}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {v.obiettivoTitolo}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold whitespace-nowrap ${labelColor}`}>
                    {giorniLabel}
                  </span>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 py-6 text-green-600 dark:text-green-400">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Nessuna verifica in scadenza</span>
          </div>
        )}
      </div>
    </>
  )
}

// Componente Card Statistica
function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  )
}

// Componente Card Minore (per "I miei minori")
function MinoreCard({ minore }) {
  const hasEntryToday = minore.hasEntryToday
  
  return (
    <Link
      to={`/entries/new/${minore._id}`}
      className={`block p-4 rounded-lg border-2 transition-all hover:shadow-md ${
        hasEntryToday
          ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">
            {minore.nomeCompleto}
          </p>
          {minore.dataNascita && (
            <p className="text-sm text-gray-500">
              {calculateAge(minore.dataNascita)} anni
            </p>
          )}
        </div>
        {hasEntryToday ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <Clock className="h-5 w-5 text-yellow-500" />
        )}
      </div>
    </Link>
  )
}

// Componente Lista Minore (per "Tutti i minori")
function MinoreListItem({ minore }) {
  return (
    <Link
      to={`/minori/${minore._id}`}
      className="flex items-center justify-between py-3 hover:bg-gray-50 dark:hover:bg-gray-700 -mx-4 px-4 transition-colors"
    >
      <div className="flex items-center">
        <span className="text-lg mr-3">
          {minore.dataNascita && calculateAge(minore.dataNascita) < 12 ? '🧒' : '👦'}
        </span>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {minore.nomeCompleto}
          </p>
          <p className="text-sm text-gray-500">
            Ref: {minore.educatoreReferente?.nome || minore.educatoreReferente?.username || 'Non assegnato'}
          </p>
        </div>
      </div>
      <ArrowRight className="h-5 w-5 text-gray-400" />
    </Link>
  )
}

// Helper calcola età
function calculateAge(birthDate) {
  const today = new Date()
  const birth = new Date(birthDate)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}
